import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, ExternalLink, Loader2, RefreshCw, Save, Search } from "lucide-react";
import { DuplicatesPanel } from "@/pages/admin/DuplicatesPanel";
import { toast } from "sonner";
import { SPORTS, fmtDate } from "@/constants/sports";
import { api, formatApiError } from "@/lib/api";

const STATUS = {
  collected: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
  failed: "text-red-400 border-red-500/30 bg-red-500/10",
  blocked: "text-orange-400 border-orange-500/30 bg-orange-500/10",
  no_roster_found: "text-amber-400 border-amber-500/30 bg-amber-500/10",
  pending: "text-slate-400 border-slate-600 bg-slate-500/10",
};
const label = (s) => (s || "pending").replace(/_/g, " ");

const Stat = ({ l, v, testId }) => (
  <div data-testid={testId} className="bg-[#131927] p-4 outline outline-1 outline-slate-800">
    <div className="eyebrow">{l}</div>
    <div className="mt-1.5 font-data text-xl font-bold text-white">{v ?? "—"}</div>
  </div>
);

const PREFIX = { softball: "sb", basketball: "wbb", lacrosse: "wlax", beach: "bvb" };

const TeamRow = ({ t, sport, running, onScrape, onSaved }) => {
  const px = PREFIX[sport];
  const [url, setUrl] = useState(t.roster_url_override || t.roster_url || "");
  const changed = url !== (t.roster_url_override || t.roster_url || "");
  const save = async () => {
    try { await api.put(`/admin/${sport}/teams/${t.slug}`, { roster_url: url }); toast.success(`${t.school} roster address saved`); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <tr data-testid={`${px}-team-row-${t.slug}`}>
      <td className="px-5 py-3">
        <div className="flex items-center gap-2">
          {t.logo_url && <img src={t.logo_url} alt="" className="h-5 w-5 object-contain" />}
          <span className="font-semibold">{t.school}</span>
        </div>
        <div className="text-xs text-slate-500">{t.conference}</div>
      </td>
      <td className="px-5 py-3">
        <span data-testid={`${px}-team-status-${t.slug}`} className={`rounded-full border px-2.5 py-0.5 font-data text-[11px] uppercase ${STATUS[t.status || "pending"]}`}>{label(t.status)}</span>
        {t.error && <div className="mt-1 max-w-[220px] text-[11px] text-slate-500">{t.error}</div>}
      </td>
      <td className="px-5 py-3 font-data">{t.players || 0}</td>
      <td className="px-5 py-3">
        <div className="flex items-center gap-2">
          <input data-testid={`${px}-team-url-${t.slug}`} value={url} onChange={(e) => setUrl(e.target.value)}
            className="h-9 w-72 rounded-md border border-slate-800 bg-[#0B0F19] px-3 text-xs outline-none focus:border-[#FF2D78]" />
          {changed && <button data-testid={`${px}-team-save-${t.slug}`} onClick={save} className="rounded-md p-2 text-slate-300 hover:bg-slate-800"><Save className="h-4 w-4" /></button>}
          {url && <a href={url} target="_blank" rel="noopener noreferrer" className="rounded-md p-2 text-slate-400 hover:bg-slate-800 hover:text-white"><ExternalLink className="h-4 w-4" /></a>}
        </div>
      </td>
      <td className="px-5 py-3 text-right">
        <button data-testid={`${px}-team-scrape-${t.slug}`} disabled={running} onClick={() => onScrape([t.slug], t.school)}
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 px-3 py-1.5 text-xs hover:border-[#FF2D78] disabled:opacity-50">
          <RefreshCw className="h-3.5 w-3.5" /> Re-scrape
        </button>
      </td>
    </tr>
  );
};

export const RosterTab = ({ sport }) => {
  const px = PREFIX[sport];
  const name = SPORTS[sport].label.toLowerCase();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const load = useCallback(() => api.get(`/admin/${sport}/teams`).then((r) => setData(r.data)), [sport]);
  useEffect(() => { load(); }, [load]);
  const running = data?.runs?.some((r) => r.status === "running");
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [running, load]);

  const scrape = async (slugs, name) => {
    try { await api.post(`/admin/${sport}/scrape`, slugs ? { slugs } : {}); toast.success(`Collecting ${name || "all rosters"}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const download = async () => {
    try {
      const r = await api.get(`/admin/${sport}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      Object.assign(document.createElement("a"), { href: url, download: sport === "beach" ? "knowhername-beach-volleyball-rosters.json" : `knowhername-${sport}-rosters-d1.json` }).click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const teams = useMemo(() => (data?.teams || []).filter((t) =>
    (status === "all" || (t.status || "pending") === status) &&
    (!q || `${t.school} ${t.conference}`.toLowerCase().includes(q.toLowerCase()))), [data, q, status]);

  if (!data) return <p className="text-sm text-slate-500">Loading {name} teams</p>;
  const s = data.summary;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="max-w-xl text-sm text-slate-400">NCAA {sport === "beach" ? "" : "Division I "}{sport === "softball" ? "" : "women's "}{name} rosters collected from each school's official athletics site. Re-scrape updates players in place; protected players are never changed.</p>
        <div className="flex gap-2">
          <button data-testid={`${px}-download-button`} onClick={download} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2.5 text-sm hover:border-white">
            <Download className="h-4 w-4" /> Download JSON
          </button>
          <button data-testid={`${px}-scrape-all-button`} onClick={() => scrape(null)} disabled={running}
            className="inline-flex items-center gap-2 rounded-full bg-[#FF2D78] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#E01F66] disabled:opacity-60">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {running ? "Collecting" : "Re-scrape all"}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 overflow-hidden rounded-lg border border-slate-800 md:grid-cols-6">
        <Stat l="Teams" v={s.teams} testId={`${px}-summary-teams`} />
        <Stat l="Collected" v={s.collected} testId={`${px}-summary-collected`} />
        <Stat l="Failed / blocked" v={s.failed + s.blocked} testId={`${px}-summary-failed`} />
        <Stat l="No roster found" v={s.no_roster_found} testId={`${px}-summary-no-roster`} />
        <Stat l="Players" v={s.players.toLocaleString()} testId={`${px}-summary-players`} />
        <Stat l="Last run" v={fmtDate(s.last_run_at)} testId={`${px}-summary-last-run`} />
      </div>
      <div className="flex flex-wrap gap-3">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input data-testid={`${px}-team-search-input`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search teams or conferences"
            className="h-11 w-full rounded-lg border border-slate-800 bg-[#131927] pl-11 pr-4 text-sm outline-none focus:border-[#FF2D78]" />
        </div>
        <div className="flex flex-wrap gap-1 rounded-lg border border-slate-800 bg-[#131927] p-1">
          {["all", "collected", "failed", "blocked", "no_roster_found", "pending"].map((v) => (
            <button key={v} data-testid={`${px}-filter-${v}`} onClick={() => setStatus(v)}
              className={`rounded-md px-3 py-1.5 font-data text-[11px] uppercase ${status === v ? "bg-[#FF2D78] text-white" : "text-slate-400 hover:text-white"}`}>{label(v)}</button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-800">
        <table className="w-full min-w-[960px] text-sm">
          <thead className="bg-[#131927]"><tr className="text-left">{["Team", "Status", "Players", "Roster page", ""].map((h) => <th key={h} className="eyebrow px-5 py-3 font-normal">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-800">
            {teams.map((t) => <TeamRow key={t.slug} t={t} sport={sport} running={running} onScrape={scrape} onSaved={load} />)}
          </tbody>
        </table>
      </div>
      <DuplicatesPanel sport={sport} px={px} refreshKey={data.runs[0]?.finished_at} />
      <div>
        <h3 className="mb-3 font-display text-xl font-extrabold uppercase">Run history</h3>
        <div className="divide-y divide-slate-800 rounded-xl border border-slate-800">
          {data.runs.map((r) => (
            <div key={r.id} data-testid={`${px}-run-${r.id}`} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-sm">
              <span className="font-data text-xs text-slate-400">{fmtDate(r.started_at)} {new Date(r.started_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
              <span className={`rounded-full border px-2.5 py-0.5 font-data text-[11px] uppercase ${r.status === "running" ? "border-blue-500/30 text-blue-400" : r.status === "failed" ? "border-red-500/30 text-red-400" : "border-emerald-500/30 text-emerald-400"}`}>{r.status}</span>
              <span className="flex-1 text-slate-300">{Array.isArray(r.scope) ? `${r.scope.length} team(s)` : "All teams"} · {r.message}</span>
              <span className="font-data text-[11px] text-slate-500">+{r.added || 0} added · {r.updated || 0} updated · {r.protected || 0} protected · {r.off_roster || 0} off roster{r.possible_duplicates ? ` · ${r.possible_duplicates} possible duplicates` : ""}</span>
            </div>
          ))}
          {!data.runs.length && <p className="p-6 text-sm text-slate-500">No runs yet.</p>}
        </div>
      </div>
    </div>
  );
};
